import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { crmAPI } from '../../services/api';
import { useCRMAuth } from '../../context/CRMAuthContext';
import CRMLayout from './CRMLayout';

// Tabs differ by role
const TABS_STAFF = [
  { key: '',         label: 'Барчаси' },
  { key: 'pending',  label: 'Жавоб кутилмоқда' },
  { key: 'taken',    label: 'Мен олган',        mineOnly: true },
  { key: 'answered', label: 'Мен жавоб берган', mineOnly: true },
];

const TABS_ADMIN = [
  { key: '',         label: 'Барчаси' },
  { key: 'pending',  label: 'Жавоб кутилмоқда' },
  { key: 'taken',    label: 'Олинган' },
  { key: 'answered', label: 'Жавоб берилди' },
];

const statusStyle = (s) => {
  if (s === 'pending')  return { bg: '#fef3c7', color: '#92400e', label: 'Жавоб кутилмоқда' };
  if (s === 'taken')    return { bg: '#ebf8ff', color: '#2b6cb0', label: 'Олинган' };
  if (s === 'answered') return { bg: '#f0fff4', color: '#276749', label: 'Жавоб берилди' };
  return { bg: '#f7fafc', color: '#718096', label: s };
};

const CRMRequests = () => {
  const { crmUser } = useCRMAuth();
  const isAdmin = crmUser?.role === 'admin';
  const tabs = isAdmin ? TABS_ADMIN : TABS_STAFF;

  const [activeTab, setActiveTab] = useState('');
  const [rawRequests, setRawRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answerMap, setAnswerMap] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const currentTabDef = tabs.find(t => t.key === activeTab) || tabs[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmAPI.getRequests(activeTab || undefined);
      setRawRequests(res.data);
    } catch {
      setRawRequests([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  // For staff "Мен олган" / "Мен жавоб берган" — filter to own requests client-side
  const requests = useMemo(() => {
    if (!isAdmin && currentTabDef?.mineOnly) {
      return rawRequests.filter(r => r.assigned_staff_id === crmUser?.id);
    }
    return rawRequests;
  }, [rawRequests, isAdmin, currentTabDef, crmUser]);

  const handleTake = async (id) => {
    try {
      await crmAPI.takeRequest(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    }
  };

  const handleAnswer = async (id) => {
    const text = answerMap[id]?.trim();
    if (!text) return;
    setSubmitting(id);
    try {
      await crmAPI.answerRequest(id, text);
      setAnswerMap(p => ({ ...p, [id]: '' }));
      setExpandedId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setSubmitting(null);
    }
  };

  // Show "by whom" badge for both roles when status is taken/answered
  const showActorBadge = (r) =>
    r.assigned_staff_name && (r.crm_status === 'taken' || r.crm_status === 'answered');

  const handleClearConfirmed = async () => {
    setShowClearConfirm(false);
    try {
      await crmAPI.clearRequests();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    }
  };

  return (
    <CRMLayout>
      <div style={{ padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a365d', margin: 0 }}>
            Маслаҳат сўровлари
          </h1>
          {isAdmin && (
            <button onClick={() => setShowClearConfirm(true)} style={clearBtnStyle}>
              Барчасини ўчириш
            </button>
          )}
        </div>

        {/* Confirmation popup */}
        {showClearConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '14px',
              padding: '2rem', maxWidth: '380px', width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center',
            }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%',
                backgroundColor: '#fed7d7', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c53030" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a365d', marginBottom: '0.6rem' }}>
                Барчасини ўчириш?
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1.75rem', lineHeight: '1.55' }}>
                Жорий кўриниши бўйича барча сўровлар яшириб қўйилади. Молия маълумотлари сақланади.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowClearConfirm(false)}
                  style={{ padding: '0.6rem 1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#718096', cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit' }}
                >
                  Бекор
                </button>
                <button
                  onClick={handleClearConfirmed}
                  style={{ padding: '0.6rem 1.5rem', border: 'none', borderRadius: '8px', backgroundColor: '#e53e3e', color: 'white', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', fontFamily: 'inherit' }}
                >
                  Ҳа, ўчириш
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.5rem 1.1rem', border: 'none', cursor: 'pointer', fontSize: '0.88rem',
                fontWeight: activeTab === tab.key ? '700' : '400',
                color: activeTab === tab.key ? '#1a365d' : '#718096',
                backgroundColor: 'transparent',
                borderBottom: activeTab === tab.key ? '2px solid #1a365d' : '2px solid transparent',
                marginBottom: '-2px', fontFamily: 'inherit',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Юкланмоқда...</div>
        ) : requests.length === 0 ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Сўровлар йўқ</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {requests.map(r => {
              const st = statusStyle(r.crm_status);
              const isMine = r.assigned_staff_id === crmUser?.id;
              const showAnswerBox = expandedId === r.id;

              return (
                <div key={r.id} style={{
                  backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                        <span style={{ backgroundColor: st.bg, color: st.color, fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '20px' }}>
                          {st.label}
                        </span>

                        {/* Actor badge — shown for both roles in Барчаси, Олинган/Мен олган, Жавоб берилди/Мен жавоб берган */}
                        {showActorBadge(r) && (
                          <span style={{ fontSize: '0.78rem', color: '#718096' }}>
                            {r.crm_status === 'taken' ? 'Олди:' : 'Жавоб берди:'}{' '}
                            <span style={{ fontWeight: '600', color: '#4a5568' }}>{r.assigned_staff_name}</span>
                          </span>
                        )}

                        <span style={{ fontSize: '0.78rem', color: '#a0aec0' }}>
                          #{r.id} · {new Date(r.created_at).toLocaleDateString('uz-UZ')}
                        </span>
                      </div>

                      <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '0.2rem' }}>
                        {r.client_name}
                        {r.client_phone && <span style={{ fontWeight: '400', color: '#718096', marginLeft: '0.5rem', fontSize: '0.9rem' }}>{r.client_phone}</span>}
                      </div>
                      <div style={{ color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.5', marginTop: '0.4rem' }}>
                        {r.description}
                      </div>

                      {r.crm_status === 'answered' && r.answer && (
                        <div style={{ marginTop: '0.75rem', backgroundColor: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '8px', padding: '0.75rem' }}>
                          <div style={{ fontSize: '0.78rem', color: '#276749', fontWeight: '600', marginBottom: '0.25rem' }}>
                            ✓ Жавоб — {r.answered_at ? new Date(r.answered_at).toLocaleString('uz-UZ') : ''}
                          </div>
                          <div style={{ color: '#276749', fontSize: '0.9rem' }}>{r.answer}</div>
                        </div>
                      )}
                    </div>

                    {/* Actions — staff only */}
                    <div style={{ flexShrink: 0 }}>
                      {r.crm_status === 'pending' && !isAdmin && (
                        <button onClick={() => handleTake(r.id)} style={btn('#3182ce')}>
                          Олиш
                        </button>
                      )}
                      {r.crm_status === 'taken' && isMine && !isAdmin && !showAnswerBox && (
                        <button onClick={() => setExpandedId(r.id)} style={btn('#38a169')}>
                          Жавоб ёзиш
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Answer box */}
                  {showAnswerBox && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                      <textarea
                        rows={4}
                        value={answerMap[r.id] || ''}
                        onChange={e => setAnswerMap(p => ({ ...p, [r.id]: e.target.value }))}
                        placeholder="Жавобни шу ерга ёзинг..."
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => setExpandedId(null)} style={btn('#718096', 'small')}>Бекор</button>
                        <button
                          onClick={() => handleAnswer(r.id)}
                          disabled={submitting === r.id || !answerMap[r.id]?.trim()}
                          style={btn(submitting === r.id ? '#a0aec0' : '#38a169', 'small')}
                        >
                          {submitting === r.id ? 'Юборилмоқда...' : 'Юбориш'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

const clearBtnStyle = {
  backgroundColor: '#fed7d7', color: '#c53030', border: 'none',
  padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.85rem',
  fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
};

const btn = (bg, size) => ({
  backgroundColor: bg, color: 'white', border: 'none',
  padding: size === 'small' ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
  borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
});

export default CRMRequests;
